import { useCarousel } from '@zillow/constellation-10';
import { useMemoizedFn } from 'ahooks';
import { useState } from 'react';

const SLIDE_SELECTOR = '[data-c11n-component="Carousel.Slide"]';

const slidesOf = (container: HTMLElement) => container.querySelectorAll<HTMLElement>(SLIDE_SELECTOR);

// The slide whose start is nearest the scroll position, or the last slide once the rail is at its
// end. Fully visible slides are not enough: a card wider than its container is never fully visible.
function nearestSlide(container: HTMLElement): number {
  const slides = slidesOf(container);
  if (slides.length === 0) return 0;
  if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 1) return slides.length - 1;

  const origin = slides[0].offsetLeft;
  const distance = (index: number) => Math.abs(slides[index].offsetLeft - origin - container.scrollLeft);
  let nearest = 0;
  for (let index = 1; index < slides.length; index++) {
    if (distance(index) < distance(nearest)) nearest = index;
  }
  return nearest;
}

/**
 * Floor plan carousel state for the page dots and the one-position arrows.
 *
 * Do not pass Constellation's `scrollToSlide` here. It calls `scrollIntoView`, which also scrolls
 * every scrollable ancestor: the HDP shell is wider than the viewport, so the whole document
 * scrolled sideways off the map column. It also re-ran on every render, because its effect
 * depends on `onVisibleSlidesChange`. Everything here scrolls only the carousel's own container.
 *
 * Keep the callbacks passed to useCarousel memoized: without `scrollToSlide`, useCarousel resets
 * the container to `scrollLeft` 0 each time they change identity, which would snap back on every
 * render.
 */
export function useFloorPlanCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const carousel = useCarousel({
    onScroll: useMemoizedFn((event: React.UIEvent<HTMLElement>) => {
      setCurrentSlide(nearestSlide(event.currentTarget));
    }),
    onVisibleSlidesChange: useMemoizedFn((indices: Array<number>) => {
      setVisibleCount(Math.max(1, indices.length));
    }),
  });

  const goToSlide = useMemoizedFn((index: number) => {
    const container = carousel.scrollContainer.current;
    const slides = container ? slidesOf(container) : undefined;
    const target = slides?.[index];
    if (!container || !slides || !target) return;

    setCurrentSlide(index);
    // Both slides share an offset parent, so the difference is the target's position in the track.
    container.scrollTo({ left: target.offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
  });

  return { carousel, currentSlide, visibleCount, goToSlide };
}
